import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { Config, ScaffoldFrontendExtensionResult } from '../types/index.js';
import { ScaffoldFrontendExtensionInputSchema } from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { writeText, fileExists, safeJoinPath } from '../utils/fs.js';
import { logger } from '../lib/logger.js';

export const scaffoldFrontendExtensionTool: Tool = {
  name: 'scaffold_frontend_extension',
  description: `Generate frontend extension infrastructure for SmartStack.

Creates:
- Extension types (ExtensionConfig, SlotProps, ColumnExtension, etc.)
- Slot/Fill system using React Context
- ExtensionProvider for configuration
- PageRegistry for page overrides
- Example extension implementation

Use type="infrastructure" for the core system, "page-slots" to add slots to existing pages,
"extension-example" for client usage examples, or "all" for everything.`,
  inputSchema: {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        enum: ['infrastructure', 'page-slots', 'extension-example', 'all'],
        description: 'Type of extension to scaffold',
      },
      target: {
        type: 'string',
        description: 'Target page or component (e.g., "UsersPage")',
      },
      options: {
        type: 'object',
        properties: {
          slots: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                location: { type: 'string' },
                contextProps: { type: 'array', items: { type: 'string' } },
              },
            },
          },
          outputPath: { type: 'string' },
          dryRun: { type: 'boolean', default: false },
          overwrite: { type: 'boolean', default: false },
        },
      },
    },
    required: ['type'],
  },
};

export async function handleScaffoldFrontendExtension(
  args: unknown,
  config: Config
): Promise<string> {
  try {
    const input = ScaffoldFrontendExtensionInputSchema.parse(args);
    const projectPath = config.smartstack.projectPath;
    const structure = await findSmartStackStructure(projectPath);

    if (!structure.web) {
      return JSON.stringify({
        success: false,
        error: 'Web project folder not found. Expected: web/, client/, or frontend/ directory.',
      });
    }

    const webPath = structure.web;
    const srcPath = safeJoinPath(webPath, 'src');
    const dryRun = input.options?.dryRun || false;
    const overwrite = input.options?.overwrite || false;

    const result: ScaffoldFrontendExtensionResult = {
      success: true,
      type: input.type,
      files: [],
      instructions: [],
      warnings: [],
    };

    switch (input.type) {
      case 'infrastructure':
        await scaffoldInfrastructure(srcPath, result, dryRun, overwrite);
        break;
      case 'page-slots':
        if (!input.target) {
          return JSON.stringify({
            success: false,
            error: 'Target page is required for page-slots type (e.g., target: "UsersPage")',
          });
        }
        await scaffoldPageSlots(srcPath, input.target, input.options?.slots || [], result, dryRun);
        break;
      case 'extension-example':
        await scaffoldExtensionExample(srcPath, result, dryRun, overwrite);
        break;
      case 'all':
        await scaffoldInfrastructure(srcPath, result, dryRun, overwrite);
        await scaffoldExtensionExample(srcPath, result, dryRun, overwrite);
        break;
    }

    return formatResult(result);
  } catch (error) {
    logger.error('scaffold_frontend_extension failed', { error });
    return JSON.stringify({
      success: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

async function scaffoldInfrastructure(
  srcPath: string,
  result: ScaffoldFrontendExtensionResult,
  dryRun: boolean,
  overwrite: boolean
): Promise<void> {
  const extensionsPath = safeJoinPath(srcPath, 'extensions');

  // 1. Types file
  const typesContent = generateTypesFile();
  await writeFileIfNeeded(
    safeJoinPath(extensionsPath, 'types.ts'),
    typesContent,
    result,
    dryRun,
    overwrite
  );

  // 2. SlotFill system
  const slotFillContent = generateSlotFillFile();
  await writeFileIfNeeded(
    safeJoinPath(extensionsPath, 'SlotFill.tsx'),
    slotFillContent,
    result,
    dryRun,
    overwrite
  );

  // 3. ExtensionContext
  const extensionContextContent = generateExtensionContextFile();
  await writeFileIfNeeded(
    safeJoinPath(extensionsPath, 'ExtensionContext.tsx'),
    extensionContextContent,
    result,
    dryRun,
    overwrite
  );

  // 4. PageRegistry
  const pageRegistryContent = generatePageRegistryFile();
  await writeFileIfNeeded(
    safeJoinPath(extensionsPath, 'PageRegistry.tsx'),
    pageRegistryContent,
    result,
    dryRun,
    overwrite
  );

  // 5. Index file
  const indexContent = generateIndexFile();
  await writeFileIfNeeded(
    safeJoinPath(extensionsPath, 'index.ts'),
    indexContent,
    result,
    dryRun,
    overwrite
  );

  result.instructions.push(
    '1. Add ExtensionProvider to SmartStackProvider.tsx',
    '2. Add extension exports to src/index.ts',
    '3. Update SmartStackConfig type in router/types.ts to include extensions?: ExtensionConfig'
  );
}

async function scaffoldPageSlots(
  srcPath: string,
  targetPage: string,
  slots: { name: string; location: string; contextProps?: string[] }[],
  result: ScaffoldFrontendExtensionResult,
  _dryRun: boolean
): Promise<void> {
  // Find the target page file
  const pagesPath = safeJoinPath(srcPath, 'pages');
  const possiblePaths = [
    safeJoinPath(pagesPath, 'admin', `${targetPage}.tsx`),
    safeJoinPath(pagesPath, `${targetPage}.tsx`),
    safeJoinPath(pagesPath, 'user', `${targetPage}.tsx`),
    safeJoinPath(pagesPath, 'business', `${targetPage}.tsx`),
  ];

  let targetPath: string | null = null;
  for (const p of possiblePaths) {
    if (await fileExists(p)) {
      targetPath = p;
      break;
    }
  }

  if (!targetPath) {
    result.warnings.push(`Page ${targetPage} not found in standard locations`);
    result.instructions.push(
      `Manually add slots to ${targetPage}:`,
      ...slots.map(s => `  - <Slot name="${s.name}" context={{ ${(s.contextProps || []).join(', ')} }} />`)
    );
    return;
  }

  result.instructions.push(
    `Add these slots to ${targetPage}:`,
    '',
    '1. Add import at top of file:',
    `   import { Slot } from '@/extensions';`,
    '',
    '2. Add slots at appropriate locations:',
    ...slots.map(s => `   - ${s.location}: <Slot name="${s.name}" context={{ ${(s.contextProps || []).join(', ')} }} />`)
  );
}

async function scaffoldExtensionExample(
  srcPath: string,
  result: ScaffoldFrontendExtensionResult,
  dryRun: boolean,
  overwrite: boolean
): Promise<void> {
  const examplesPath = safeJoinPath(srcPath, 'extensions', 'examples');

  const exampleContent = generateUserExtensionExample();
  await writeFileIfNeeded(
    safeJoinPath(examplesPath, 'UserExtensionExample.tsx'),
    exampleContent,
    result,
    dryRun,
    overwrite
  );

  result.instructions.push(
    'Example extension created. To use:',
    '1. Import in your client app',
    '2. Add to SmartStackProvider config.extensions'
  );
}

async function writeFileIfNeeded(
  filePath: string,
  content: string,
  result: ScaffoldFrontendExtensionResult,
  dryRun: boolean,
  overwrite: boolean
): Promise<void> {
  const exists = await fileExists(filePath);

  if (exists && !overwrite) {
    result.files.push({ path: filePath, content, action: 'skipped' });
    result.warnings.push(`File already exists: ${filePath} (use overwrite: true to replace)`);
    return;
  }

  if (!dryRun) {
    await writeText(filePath, content);
  }

  result.files.push({
    path: filePath,
    content,
    action: exists ? 'modified' : 'created',
  });
}

function formatResult(result: ScaffoldFrontendExtensionResult): string {
  const lines: string[] = [];

  lines.push(`# Frontend Extension Scaffold Result`);
  lines.push('');
  lines.push(`**Status**: ${result.success ? '✅ Success' : '❌ Failed'}`);
  lines.push(`**Type**: ${result.type}`);
  lines.push('');

  if (result.files.length > 0) {
    lines.push('## Files');
    for (const file of result.files) {
      const icon = file.action === 'created' ? '✅' : file.action === 'modified' ? '📝' : '⏭️';
      lines.push(`${icon} \`${file.path}\` (${file.action})`);
    }
    lines.push('');
  }

  if (result.warnings.length > 0) {
    lines.push('## Warnings');
    for (const warning of result.warnings) {
      lines.push(`⚠️ ${warning}`);
    }
    lines.push('');
  }

  if (result.instructions.length > 0) {
    lines.push('## Next Steps');
    for (const instruction of result.instructions) {
      lines.push(instruction);
    }
  }

  return lines.join('\n');
}

// ============================================================================
// File Content Generators
// ============================================================================

function generateTypesFile(): string {
  return `import type { ComponentType, ReactNode } from 'react';

/**
 * Extension configuration for SmartStack frontend
 */
export interface ExtensionConfig {
  /**
   * Page component overrides
   * Key: NavRoute path (e.g., 'platform.administration.users')
   * Value: React component to render instead of the default page
   */
  pages?: Record<string, ComponentType<PageProps>>;

  /**
   * Slot content injections
   * Key: Slot name (e.g., 'users.form.fields.after')
   * Value: React component, render function, or array of components
   */
  slots?: Record<string, SlotContent>;

  /**
   * Table column extensions
   * Key: Table identifier (e.g., 'users', 'roles')
   */
  tableColumns?: Record<string, ColumnExtension[]>;

  /**
   * Form field extensions
   * Key: Form identifier (e.g., 'user-form', 'role-form')
   */
  formFields?: Record<string, FormFieldExtension[]>;

  /**
   * Event handlers for extension hooks
   */
  hooks?: ExtensionHooks;
}

export interface PageProps {
  pageKey: string;
  params?: Record<string, string>;
}

export type SlotContent =
  | ComponentType<SlotProps>
  | ((props: SlotProps) => ReactNode)
  | ReactNode
  | SlotContent[];

export interface SlotProps {
  slotName: string;
  context?: Record<string, unknown>;
}

export interface ColumnExtension {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  render: (item: Record<string, unknown>, context: ColumnContext) => ReactNode;
  position?: 'before' | 'after';
  insertAfter?: string;
  insertBefore?: string;
}

export interface ColumnContext {
  tableId: string;
  rowIndex: number;
}

export interface FormFieldExtension {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'textarea' | 'custom';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  validate?: (value: unknown) => string | undefined;
  render?: (props: FormFieldProps) => ReactNode;
  position?: 'before' | 'after';
  insertAfter?: string;
  insertBefore?: string;
}

export interface FormFieldProps {
  name: string;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  formData: Record<string, unknown>;
}

export interface ExtensionHooks {
  onPageLoad?: (pageKey: string) => void | Promise<void>;
  onFormSubmit?: (formId: string, data: Record<string, unknown>) => Record<string, unknown> | Promise<Record<string, unknown>>;
  onTableRowClick?: (tableId: string, row: Record<string, unknown>) => void;
  onBeforeSave?: (entityType: string, data: Record<string, unknown>) => Record<string, unknown> | Promise<Record<string, unknown>>;
  onAfterSave?: (entityType: string, data: Record<string, unknown>, result: unknown) => void | Promise<void>;
}

export type SlotName =
  | 'users.header.actions'
  | 'users.table.columns.before'
  | 'users.table.columns.after'
  | 'users.table.row.actions'
  | 'users.form.fields.before'
  | 'users.form.fields.after'
  | 'users.card.content'
  | 'users.detail.header'
  | 'users.detail.tabs.after'
  | 'users.detail.sidebar'
  | 'roles.header.actions'
  | 'roles.table.columns.after'
  | 'roles.form.fields.after'
  | 'permissions.header.actions'
  | string;

export interface PageRegistryEntry {
  key: string;
  defaultComponent: ComponentType<PageProps>;
  slots: SlotName[];
}

export interface ExtensionContextValue {
  config: ExtensionConfig;
  getPage: (key: string) => ComponentType<PageProps> | undefined;
  getSlotContent: (slotName: string) => SlotContent | undefined;
  getTableColumns: (tableId: string) => ColumnExtension[];
  getFormFields: (formId: string) => FormFieldExtension[];
  hasExtension: (key: string) => boolean;
}
`;
}

function generateSlotFillFile(): string {
  return `import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
  type ComponentType,
  isValidElement,
  Fragment,
} from 'react';
import type { SlotContent, SlotProps } from './types';

interface SlotFillContextValue {
  fills: Map<string, SlotContent[]>;
  registerFill: (slotName: string, content: SlotContent) => void;
  unregisterFill: (slotName: string, content: SlotContent) => void;
  getSlotContent: (slotName: string) => SlotContent[];
}

const SlotFillContext = createContext<SlotFillContextValue | null>(null);

export function useSlotFill(): SlotFillContextValue {
  const context = useContext(SlotFillContext);
  if (!context) {
    throw new Error('useSlotFill must be used within a SlotFillProvider');
  }
  return context;
}

interface SlotFillProviderProps {
  children: ReactNode;
  initialFills?: Record<string, SlotContent>;
}

export function SlotFillProvider({ children, initialFills }: SlotFillProviderProps) {
  const fills = useMemo(() => {
    const map = new Map<string, SlotContent[]>();
    if (initialFills) {
      Object.entries(initialFills).forEach(([slotName, content]) => {
        const existing = map.get(slotName) || [];
        if (Array.isArray(content)) {
          map.set(slotName, [...existing, ...content]);
        } else {
          map.set(slotName, [...existing, content]);
        }
      });
    }
    return map;
  }, [initialFills]);

  const contextValue = useMemo<SlotFillContextValue>(
    () => ({
      fills,
      registerFill: (slotName: string, content: SlotContent) => {
        const existing = fills.get(slotName) || [];
        fills.set(slotName, [...existing, content]);
      },
      unregisterFill: (slotName: string, content: SlotContent) => {
        const existing = fills.get(slotName) || [];
        fills.set(slotName, existing.filter((c) => c !== content));
      },
      getSlotContent: (slotName: string) => fills.get(slotName) || [],
    }),
    [fills]
  );

  return <SlotFillContext.Provider value={contextValue}>{children}</SlotFillContext.Provider>;
}

interface SlotComponentProps {
  name: string;
  context?: Record<string, unknown>;
  children?: ReactNode;
  fallback?: ReactNode;
  wrapper?: ComponentType<{ children: ReactNode }>;
}

export function Slot({ name, context = {}, children, fallback, wrapper: Wrapper }: SlotComponentProps) {
  const slotFill = useContext(SlotFillContext);
  const slotContent = slotFill?.getSlotContent(name) || [];

  const props: SlotProps = useMemo(
    () => ({ slotName: name, context }),
    [name, context]
  );

  const renderedContent = useMemo(() => {
    if (slotContent.length === 0) {
      return fallback || null;
    }

    return slotContent.map((content, index) => {
      const key = \`\${name}-\${index}\`;
      return (
        <Fragment key={key}>
          {renderSlotContent(content, props)}
        </Fragment>
      );
    });
  }, [slotContent, name, props, fallback]);

  const finalContent = (
    <>
      {renderedContent}
      {children}
    </>
  );

  if (Wrapper) {
    return <Wrapper>{finalContent}</Wrapper>;
  }

  return <>{finalContent}</>;
}

interface FillProps {
  slot: string;
  children: ReactNode;
}

export function Fill({ slot, children }: FillProps) {
  const { registerFill, unregisterFill } = useSlotFill();

  useMemo(() => {
    registerFill(slot, children);
    return () => unregisterFill(slot, children);
  }, [slot, children, registerFill, unregisterFill]);

  return null;
}

function renderSlotContent(content: SlotContent, props: SlotProps): ReactNode {
  if (content === null || content === undefined) {
    return null;
  }

  if (Array.isArray(content)) {
    return (
      <>
        {content.map((item, index) => (
          <Fragment key={index}>{renderSlotContent(item, props)}</Fragment>
        ))}
      </>
    );
  }

  if (isValidElement(content)) {
    return content;
  }

  if (typeof content === 'function') {
    const Component = content as ComponentType<SlotProps>;
    return <Component {...props} />;
  }

  return content as ReactNode;
}

export function useSlotHasContent(slotName: string): boolean {
  const slotFill = useContext(SlotFillContext);
  return (slotFill?.getSlotContent(slotName)?.length || 0) > 0;
}

export function createSlotFill<T extends Record<string, unknown> = Record<string, unknown>>(slotName: string) {
  function TypedSlot(props: Omit<SlotComponentProps, 'name'> & { context?: T }) {
    return <Slot {...props} name={slotName} />;
  }
  TypedSlot.displayName = \`Slot(\${slotName})\`;

  function TypedFill({ children }: { children: ReactNode }) {
    return <Fill slot={slotName} children={children} />;
  }
  TypedFill.displayName = \`Fill(\${slotName})\`;

  return { Slot: TypedSlot, Fill: TypedFill, slotName };
}
`;
}

function generateExtensionContextFile(): string {
  return `import { createContext, useContext, useMemo, type ReactNode, type ComponentType } from 'react';
import type {
  ExtensionConfig,
  ExtensionContextValue,
  PageProps,
  SlotContent,
  ColumnExtension,
  FormFieldExtension,
} from './types';
import { SlotFillProvider } from './SlotFill';

const ExtensionContext = createContext<ExtensionContextValue | null>(null);

export function useExtensions(): ExtensionContextValue {
  const context = useContext(ExtensionContext);
  if (!context) {
    return {
      config: {},
      getPage: () => undefined,
      getSlotContent: () => undefined,
      getTableColumns: () => [],
      getFormFields: () => [],
      hasExtension: () => false,
    };
  }
  return context;
}

export function usePageOverride(pageKey: string): ComponentType<PageProps> | undefined {
  const { getPage } = useExtensions();
  return getPage(pageKey);
}

export function useTableColumnExtensions(tableId: string): ColumnExtension[] {
  const { getTableColumns } = useExtensions();
  return getTableColumns(tableId);
}

export function useFormFieldExtensions(formId: string): FormFieldExtension[] {
  const { getFormFields } = useExtensions();
  return getFormFields(formId);
}

export function useHasExtension(key: string): boolean {
  const { hasExtension } = useExtensions();
  return hasExtension(key);
}

interface ExtensionProviderProps {
  config?: ExtensionConfig;
  children: ReactNode;
}

export function ExtensionProvider({ config = {}, children }: ExtensionProviderProps) {
  const contextValue = useMemo<ExtensionContextValue>(() => {
    const { pages = {}, slots = {}, tableColumns = {}, formFields = {} } = config;

    return {
      config,
      getPage: (key: string): ComponentType<PageProps> | undefined => pages[key],
      getSlotContent: (slotName: string): SlotContent | undefined => slots[slotName],
      getTableColumns: (tableId: string): ColumnExtension[] => tableColumns[tableId] || [],
      getFormFields: (formId: string): FormFieldExtension[] => formFields[formId] || [],
      hasExtension: (key: string): boolean =>
        key in pages || key in slots || key in tableColumns || key in formFields,
    };
  }, [config]);

  return (
    <ExtensionContext.Provider value={contextValue}>
      <SlotFillProvider initialFills={config.slots}>
        {children}
      </SlotFillProvider>
    </ExtensionContext.Provider>
  );
}
`;
}

function generatePageRegistryFile(): string {
  return `import { type ComponentType } from 'react';
import type { PageProps, PageRegistryEntry, SlotName } from './types';
import { usePageOverride } from './ExtensionContext';

class PageRegistryClass {
  private pages: Map<string, PageRegistryEntry> = new Map();

  register(key: string, component: ComponentType<PageProps>, slots: SlotName[] = []): void {
    this.pages.set(key, { key, defaultComponent: component, slots });
  }

  get(key: string): PageRegistryEntry | undefined {
    return this.pages.get(key);
  }

  getAll(): PageRegistryEntry[] {
    return Array.from(this.pages.values());
  }

  has(key: string): boolean {
    return this.pages.has(key);
  }

  getSlots(key: string): SlotName[] {
    return this.pages.get(key)?.slots || [];
  }
}

export const PageRegistry = new PageRegistryClass();

export function withPageOverride<P extends PageProps>(
  pageKey: string,
  DefaultComponent: ComponentType<P>
): ComponentType<P> {
  function OverridablePage(props: P) {
    const OverrideComponent = usePageOverride(pageKey);
    if (OverrideComponent) {
      return <OverrideComponent {...props} />;
    }
    return <DefaultComponent {...props} />;
  }
  OverridablePage.displayName = \`Overridable(\${pageKey})\`;
  return OverridablePage;
}

export function useActivePage(pageKey: string): ComponentType<PageProps> | undefined {
  const override = usePageOverride(pageKey);
  const entry = PageRegistry.get(pageKey);
  return override || entry?.defaultComponent;
}

interface ExtendablePageProps {
  pageKey: string;
  defaultComponent: ComponentType<PageProps>;
  params?: Record<string, string>;
}

export function ExtendablePage({ pageKey, defaultComponent: DefaultComponent, params }: ExtendablePageProps) {
  const OverrideComponent = usePageOverride(pageKey);
  const props: PageProps = { pageKey, params };

  if (OverrideComponent) {
    return <OverrideComponent {...props} />;
  }
  return <DefaultComponent {...props} />;
}

export const PAGE_KEYS = {
  USERS_LIST: 'platform.administration.users',
  USERS_DETAIL: 'platform.administration.users.detail',
  ROLES_LIST: 'platform.administration.permissions.roles',
  ROLES_DETAIL: 'platform.administration.permissions.roles.detail',
  PERMISSIONS_LIST: 'platform.administration.permissions.permissions',
  PERMISSIONS_DETAIL: 'platform.administration.permissions.permissions.detail',
  ASSIGNMENTS: 'platform.administration.permissions.assignments',
  AI_DASHBOARD: 'platform.administration.ai.dashboard',
  AI_PROMPTS_LIST: 'platform.administration.ai.prompts',
  AI_PROMPTS_DETAIL: 'platform.administration.ai.prompts.detail',
  WORKFLOWS_LIST: 'platform.administration.workflows.list',
  WORKFLOWS_DETAIL: 'platform.administration.workflows.detail',
  EMAIL_TEMPLATES_LIST: 'platform.administration.workflows.email-templates',
  EMAIL_TEMPLATES_DETAIL: 'platform.administration.workflows.email-templates.detail',
  TICKETS_LIST: 'platform.support.tickets',
  TICKETS_DETAIL: 'platform.support.tickets.detail',
  SUPPORT_DASHBOARD: 'platform.support.dashboard',
  USER_PROFILE: 'personal.myspace.profile',
  USER_PREFERENCES: 'personal.myspace.preferences',
  USER_DASHBOARD: 'personal.myspace.dashboard',
  MY_TICKETS_LIST: 'business.support-client.my-tickets',
  MY_TICKETS_DETAIL: 'business.support-client.my-tickets.detail',
} as const;

export type PageKey = typeof PAGE_KEYS[keyof typeof PAGE_KEYS];
`;
}

function generateIndexFile(): string {
  return `/**
 * SmartStack Frontend Extension System
 * @packageDocumentation
 */

// Types
export type {
  ExtensionConfig,
  PageProps,
  SlotContent,
  SlotProps,
  ColumnExtension,
  ColumnContext,
  FormFieldExtension,
  FormFieldProps,
  ExtensionHooks,
  SlotName,
  PageRegistryEntry,
  ExtensionContextValue,
} from './types';

// Extension Context & Provider
export {
  ExtensionProvider,
  useExtensions,
  usePageOverride,
  useTableColumnExtensions,
  useFormFieldExtensions,
  useHasExtension,
} from './ExtensionContext';

// Slot/Fill System
export {
  SlotFillProvider,
  Slot,
  Fill,
  useSlotFill,
  useSlotHasContent,
  createSlotFill,
} from './SlotFill';

// Page Registry
export {
  PageRegistry,
  withPageOverride,
  useActivePage,
  ExtendablePage,
  PAGE_KEYS,
  type PageKey,
} from './PageRegistry';
`;
}

function generateUserExtensionExample(): string {
  return `/**
 * Example: Extending the Users page with custom fields
 *
 * Usage in client app:
 * \`\`\`tsx
 * import { SmartStackProvider } from '@atlashub/smartstack';
 * import { UserExtensionFields, userTableColumns } from './extensions/UserExtensions';
 *
 * const config = {
 *   apiUrl: 'https://api.mycompany.com',
 *   extensions: {
 *     slots: {
 *       'users.form.fields.after': UserExtensionFields,
 *     },
 *     tableColumns: {
 *       users: userTableColumns,
 *     },
 *   },
 * };
 * \`\`\`
 */

import { type ReactNode } from 'react';
import type { SlotProps, ColumnExtension, FormFieldExtension } from '../types';

interface UserExtensionData {
  employeeNumber?: string;
  departmentId?: string;
  department?: { id: string; name: string };
  hireDate?: string;
  officeLocation?: string;
}

export function UserExtensionFields({ context }: SlotProps): ReactNode {
  const formData = context?.formData as Record<string, unknown> | undefined;
  const onChange = context?.onChange as ((field: string, value: unknown) => void) | undefined;
  const disabled = context?.disabled as boolean | undefined;

  const ext = (formData?.ext || {}) as UserExtensionData;

  const handleChange = (field: keyof UserExtensionData, value: string) => {
    if (onChange) {
      onChange('ext', { ...ext, [field]: value });
    }
  };

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h4 className="text-sm font-medium text-muted-foreground">Custom Fields</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="employeeNumber" className="text-sm font-medium">Employee Number</label>
          <input
            id="employeeNumber"
            type="text"
            className="w-full px-3 py-2 border rounded-md"
            value={ext.employeeNumber || ''}
            onChange={(e) => handleChange('employeeNumber', e.target.value)}
            disabled={disabled}
            placeholder="EMP-001"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="departmentId" className="text-sm font-medium">Department</label>
          <select
            id="departmentId"
            className="w-full px-3 py-2 border rounded-md"
            value={ext.departmentId || ''}
            onChange={(e) => handleChange('departmentId', e.target.value)}
            disabled={disabled}
          >
            <option value="">Select department...</option>
            <option value="engineering">Engineering</option>
            <option value="sales">Sales</option>
            <option value="marketing">Marketing</option>
            <option value="hr">Human Resources</option>
            <option value="finance">Finance</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export const userTableColumns: ColumnExtension[] = [
  {
    key: 'employeeNumber',
    label: 'Employee #',
    sortable: true,
    width: '120px',
    render: (item) => {
      const ext = (item.ext || {}) as UserExtensionData;
      return ext.employeeNumber || '-';
    },
  },
  {
    key: 'department',
    label: 'Department',
    sortable: true,
    render: (item) => {
      const ext = (item.ext || {}) as UserExtensionData;
      return ext.department?.name || '-';
    },
  },
];

export const userFormFields: FormFieldExtension[] = [
  {
    name: 'ext.employeeNumber',
    label: 'Employee Number',
    type: 'text',
    placeholder: 'EMP-001',
    position: 'after',
  },
  {
    name: 'ext.departmentId',
    label: 'Department',
    type: 'select',
    options: [
      { value: 'engineering', label: 'Engineering' },
      { value: 'sales', label: 'Sales' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'hr', label: 'Human Resources' },
      { value: 'finance', label: 'Finance' },
    ],
    position: 'after',
  },
];

export const completeUserExtensionConfig = {
  slots: { 'users.form.fields.after': UserExtensionFields },
  tableColumns: { users: userTableColumns },
  formFields: { 'user-form': userFormFields },
};
`;
}
